import {faEnvelopeDot, faTypewriter} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Box, Button, Divider, Group, Paper, Stack} from '@mantine/core';
import {FC, useCallback, useMemo, useState} from 'react';
import {SubmitHandler, useFormContext} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncRetry} from 'react-use';
import {CodesService} from '../../api/generated/CodesService';
import {EvaluationsService} from '../../api/generated/EvaluationsService';
import {JobsService} from '../../api/generated/JobsService';
import {AsyncStateContainer} from '../../components/async-state-container';
import {createForm} from '../../forms';
import {useNotification} from '../../hooks/use-notifications';
import {useOptions} from '../../hooks/use-options';
import {ROUTES} from '../../routes';
import {DropdownOptionItem} from '../../types';
import {useJobStore} from '../job-store';
import {useEvaluationStore} from './evaluation-store';
import {JobEvaluationDetails} from './job-evalutation-details';
import {evaluationSchema, EvaluationSchema} from './schemas';
import {AnchorIcon} from '../../components/anchor-icon.tsx';
import {JobTypes} from '../../api/generated/enums.ts';

const Form = createForm<EvaluationSchema>();

export const JobEvaluationUpdate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);
  const [emailEvaluationLoading, setEmailEvaluationLoading] =
    useState<boolean>(false);
  const [emailSalesEvaluationLoading, setEmailSalesEvaluationLoading] =
    useState<boolean>(false);

  const fetchBlankJobReport = useAsyncRetry(async () => {
    const {data} = await JobsService.getBlankJobForm({
      id,
    });
    return data;
  }, [id]);

  const blankJobReportUri = useMemo(
    () => fetchBlankJobReport.value?.url || undefined,
    [fetchBlankJobReport.value?.url],
  );

  const [blankJobReportLoading, setBlankJobReportLoading] =
    useState<boolean>(false);

  const {fetchJob, isJobReadonly, job} = useJobStore((state) => ({
    fetchJob: state.fetchJob,
    isJobReadonly: state.isJobReadonly,
    job: state.job,
  }));

  const {
    data: evaluation,
    fetch: fetchEvaluation,
    loading: evaluationLoading,
  } = useEvaluationStore((state) => ({
    data: state.data,
    fetch: state.fetch,
    loading: state.loading,
  }));

  useAsync(async () => await fetchEvaluation(id), [fetchEvaluation, id]);

  const onSubmit = useCallback<SubmitHandler<EvaluationSchema>>(
    async (value) => {
      if (evaluation && evaluation.id) {
        const response = await EvaluationsService.update({
          id: evaluation.id,
          body: value,
        });

        if (response.hasErrors) {
          return response;
        }

        notifications.success('Evaluation Updated');

        await fetchJob(id);
        await fetchEvaluation(id);
      }
    },
    [evaluation, fetchEvaluation, fetchJob, id, notifications],
  );

  const emailEvaluation = useCallback(async () => {
    setEmailEvaluationLoading(true);
    try {
      const response = await EvaluationsService.generateEvaluationForm({
        id: Number(evaluation?.id),
        body: {
          sendToSales: false,
        },
      });
      if (response.hasErrors) {
        notifications.error('There was a problem sending the Evaluation Form.');
        return;
      }
      notifications.success('The Evaluation Form has been sent.');
      await fetchEvaluation(id);
    } catch {
      notifications.error('There was a problem sending the Evaluation Form.');
    } finally {
      setEmailEvaluationLoading(false);
    }
  }, [evaluation?.id, fetchEvaluation, id, notifications]);

  const emailSalesEvaluation = useCallback(async () => {
    setEmailSalesEvaluationLoading(true);
    try {
      const response = await EvaluationsService.generateEvaluationForm({
        id: Number(evaluation?.id),
        body: {
          sendToSales: true,
        },
      });
      if (response.hasErrors) {
        notifications.error('There was a problem sending the Evaluation Form.');
        return;
      }
      notifications.success('The Evaluation Form has been sent.');
      await fetchEvaluation(id);
    } catch {
      notifications.error('There was a problem sending the Evaluation Form.');
    } finally {
      setEmailSalesEvaluationLoading(false);
    }
  }, [evaluation?.id, fetchEvaluation, id, notifications]);

  const onReviewClick = useCallback(() => {
    navigate(ROUTES.ADMIN.JOB_REVIEWS.INDEX.buildPath({jobId: id}));
  }, [id, navigate]);

  const downloadBlankJobReport = useCallback(async () => {
    setBlankJobReportLoading(true);
    const response = await JobsService.generateBlankJobForm({id});
    if (response.hasErrors) {
      notifications.error(
        'There was a problem generating the Blank Job Report.',
      );
      setBlankJobReportLoading(false);
      return;
    }
    notifications.success('The Blank Job Report is ready to open.');
    fetchBlankJobReport.retry();
    setBlankJobReportLoading(false);
  }, [fetchBlankJobReport, id, notifications]);

  return (
    <Stack gap='xl'>
      <Paper withBorder shadow='sm'>
        <AsyncStateContainer loading={evaluationLoading}>
          <JobEvaluationDetails evaluation={evaluation} />
          <Divider />
          <Form
            schema={evaluationSchema}
            defaultValues={evaluation ?? undefined}
            onSubmit={onSubmit}
          >
            <Form.Container withBorder={false} shadow='sm'>
              <FormFields isReadonly={isJobReadonly} />
              <Group mt={24} justify='flex-end'>
                {!blankJobReportUri ? (
                  <Button
                    onClick={downloadBlankJobReport}
                    loading={blankJobReportLoading}
                    variant='outline'
                  >
                    <FontAwesomeIcon icon={faTypewriter} /> &nbsp; Blank Job
                    Report
                  </Button>
                ) : (
                  <Button variant='outline'>
                    <AnchorIcon
                      href={blankJobReportUri}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Blank Job Report
                    </AnchorIcon>
                  </Button>
                )}
                {job?.jobType === JobTypes['Non-M&M'] && (
                  <Button
                    onClick={emailSalesEvaluation}
                    loading={emailSalesEvaluationLoading}
                    variant='outline'
                  >
                    <FontAwesomeIcon icon={faEnvelopeDot} /> &nbsp; Send
                    Evaluation to Sales
                  </Button>
                )}
                <Button
                  onClick={emailEvaluation}
                  loading={emailEvaluationLoading}
                  variant='outline'
                >
                  <FontAwesomeIcon icon={faEnvelopeDot} /> &nbsp; Evaluation
                  Form
                </Button>
                <Divider orientation='vertical' />
                <Button onClick={onReviewClick}>M&M Review</Button>
                <Form.Submit disabled={isJobReadonly}>
                  Save Evaluation
                </Form.Submit>
              </Group>
            </Form.Container>
          </Form>
        </AsyncStateContainer>
      </Paper>
    </Stack>
  );
};

type FormFieldsProps = {
  isReadonly: boolean;
};

const FormFields: FC<FormFieldsProps> = ({isReadonly}) => {
  const customerRejectionReasonOptions =
    useOptions(CodesService.getCustomerRejectionReasonOptions).value ?? [];
  const nonRepairableReasonOptions =
    useOptions(CodesService.getNonRepairableReasonOptions).value ?? [];

  const {job} = useJobStore((state) => ({job: state.job}));
  const {watch} = useFormContext<EvaluationSchema>();
  const watchIsRepairable = watch('isRepairable');
  const watchIsQuoteApproved = watch('isQuoteApproved');

  return (
    <Stack gap='xs'>
      <Form.Row justify='space-between'>
        <Box maw={150}>
          <Form.Checkbox
            name='isCompleted'
            label='Evaluation Complete'
            disabled={isReadonly}
          />
        </Box>
        <Form.Select
          name='isRepairable'
          label='Repairable'
          data={boolOptions}
          disabled={isReadonly}
        />
        <Form.Select
          name='nonRepairableReason'
          label='Non Repairable Reason'
          data={nonRepairableReasonOptions}
          clearable={!isReadonly}
          disabled={watchIsRepairable || isReadonly}
        />
        <Form.TextArea
          name='nonRepairableRemarks'
          label='Non Repairable Remarks'
          disabled={watchIsRepairable || isReadonly}
        />
      </Form.Row>
      <Form.Row justify='space-between'>
        <Box maw={150}>
          <Form.Checkbox
            name='isQuoted'
            label='Job Quoted'
            disabled={isReadonly}
          />
        </Box>
        <Stack>
          <Form.Select
            name='isQuoteApproved'
            label='Quote Approved'
            data={boolOptions}
            disabled={isReadonly}
          />
          <Form.TextInput
            name='quoteReference'
            label='Quote Reference'
            disabled={isReadonly}
          />
        </Stack>
        <Form.Select
          name='quoteDisapprovalReason'
          label='Customer Rejection Reason'
          data={customerRejectionReasonOptions}
          disabled={watchIsQuoteApproved || isReadonly}
          clearable={!isReadonly}
        />
        <Form.TextArea
          name='quoteRemarks'
          label='Quote Remarks'
          disabled={watchIsQuoteApproved || isReadonly}
        />
      </Form.Row>
      {job?.jobType === JobTypes['Non-M&M'] && (
        <Form.TextArea
          name='workPerformedSummary'
          label='Work Performed Summary'
          disabled={isReadonly}
          minRows={4}
          description='Enter notes about the work being performed on this Non-M&M job'
        />
      )}
    </Stack>
  );
};

const boolOptions: DropdownOptionItem[] = [
  {
    value: 'true',
    label: 'Yes',
  },
  {
    value: 'false',
    label: 'No',
  },
];